import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProcessExecutionsService, ProcessExecution, JobDefinition } from 'src/app/services/process-executions/process-executions.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-proceso-detalle',
  templateUrl: './proceso-detalle.component.html',
  styleUrls: ['./proceso-detalle.component.css']
})
export class ProcesoDetalleComponent implements OnInit, OnDestroy {
  runId: string = '';
  run: ProcessExecution | null = null;
  jobDef: JobDefinition | null = null;
  events: any[] = [];
  items: any[] = [];
  itemsTotal = 0;
  eventsTotal = 0;

  itemsResultFilter = '';
  eventsLevelFilter = '';

  progressTimer: any = null;
  eventsTimer: any = null;
  itemsTimer: any = null;
  loadingItems = false;
  error: string | null = null;
  cancelling = false;
  deleting = false;

  // Polling intervals
  private PROGRESS_MS = 2000;
  private EVENTS_MS = 3000;
  private ITEMS_MS = 5000;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public processSvc: ProcessExecutionsService
  ) {}

  async ngOnInit() {
    this.runId = this.route.snapshot.params['id'];
    await this.loadInitial();
    this.startPolling();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  async loadInitial() {
    try {
      const r = await this.processSvc.get(this.runId);
      this.run = r?.run || null;
      this.jobDef = r?.job_definition || null;
      await this.loadEvents();
      await this.loadItems();
    } catch (e: any) {
      this.error = e?.message || 'Error al cargar';
    }
  }

  startPolling() {
    this.progressTimer = setInterval(async () => {
      if (!this.run) return;
      // Si ya terminó, dejar de hacer polling de progress
      if (['success', 'failed', 'cancelled', 'partial'].includes(this.run.status)) {
        this.stopPolling();
        // Una última actualización de items al cerrar
        this.loadItems();
        return;
      }
      try {
        const p = await this.processSvc.progress(this.runId);
        if (p && this.run) {
          this.run = { ...this.run, ...p };
        }
      } catch (e) { /* silencioso */ }
    }, this.PROGRESS_MS);

    this.eventsTimer = setInterval(() => this.loadEvents(), this.EVENTS_MS);
    // Polling de items para actualizar la tabla mientras el job corre
    this.itemsTimer = setInterval(() => this.loadItems(), this.ITEMS_MS);
  }

  stopPolling() {
    if (this.progressTimer) { clearInterval(this.progressTimer); this.progressTimer = null; }
    if (this.eventsTimer) { clearInterval(this.eventsTimer); this.eventsTimer = null; }
    if (this.itemsTimer) { clearInterval(this.itemsTimer); this.itemsTimer = null; }
  }

  async loadEvents() {
    try {
      const r = await this.processSvc.events(this.runId, this.eventsLevelFilter || undefined, 1, 200);
      this.events = r?.items || [];
      this.eventsTotal = r?.total || 0;
    } catch (e) { /* silencioso */ }
  }

  async loadItems(page = 1) {
    this.loadingItems = true;
    try {
      const r = await this.processSvc.items(this.runId, this.itemsResultFilter || undefined, page, 50);
      this.items = r?.items || [];
      this.itemsTotal = r?.total || 0;
    } catch (e: any) {
      this.error = e?.message;
    } finally {
      this.loadingItems = false;
    }
  }

  async cancel() {
    if (!confirm('¿Solicitar cancelación de esta corrida? El worker se detendrá en el próximo checkpoint.')) return;
    this.cancelling = true;
    try {
      await this.processSvc.cancel(this.runId);
      if (this.run) this.run = { ...this.run, cancel_requested: true };
    } catch (e: any) {
      this.error = e?.message;
    } finally {
      this.cancelling = false;
    }
  }

  async deleteRun() {
    if (!this.run) return;
    if (this.run.status === 'running' && !this.run.cancel_requested) {
      alert('Esta corrida está en estado "running". Cancelala primero (botón naranja) y volvé a intentar.');
      return;
    }
    const label = this.run.job_label || this.run.job_key;
    if (!confirm(`¿Eliminar la corrida "${label}"?\n\nEsto borra el run, items y log de eventos. No se puede deshacer.`)) return;
    this.deleting = true;
    this.stopPolling();
    try {
      await this.processSvc.delete(this.runId);
      this.router.navigate(['/console/procesos-masivos']);
    } catch (e: any) {
      this.error = `Error eliminando: ${e?.error?.msg || e?.message || ''}`;
      this.deleting = false;
      this.startPolling();
    }
  }

  downloadXlsx() {
    const token = localStorage.getItem('x-token') || '';
    const url = this.processSvc.downloadDetailXlsxUrl(this.runId);
    // Usamos fetch para inyectar el token, después blob → download
    fetch(url, { headers: { authorization: token } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${this.run?.job_key || 'corrida'}_${this.runId}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(err => { this.error = 'Error descargando XLSX: ' + err?.message; });
  }

  goBack() { this.router.navigate(['/console/procesos-masivos']); }

  statusBadgeClass(s?: string) {
    switch (s) {
      case 'running': return 'bg-blue-100 text-blue-700';
      case 'success': return 'bg-green-100 text-green-700';
      case 'partial': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-200 text-gray-700';
      case 'queued': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  fmtDuration(ms?: number | null) {
    if (ms == null) return '-';
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(1)}s`;
    if (s < 3600) return `${(s / 60).toFixed(1)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  }

  progressPct(): number {
    if (!this.run?.progress) return 0;
    const p = this.run.progress;
    if (!p.total) return 0;
    return Math.min(100, Math.round(((p.processed || 0) / p.total) * 100));
  }

  etaText(): string {
    const eta = this.run?.eta_ms;
    if (!eta || eta < 0) return '-';
    return this.fmtDuration(eta);
  }

  levelColor(level: string) {
    switch (level) {
      case 'error': return 'text-red-600 bg-red-50';
      case 'warn': return 'text-yellow-700 bg-yellow-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  }

  resultBadge(result: string) {
    switch (result) {
      case 'ok': return 'bg-green-100 text-green-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'skipped': return 'bg-yellow-100 text-yellow-700';
      case 'noop': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  getItemColumns() {
    return this.jobDef?.item_columns || [
      { key: 'ref_code', header: 'Ref' },
      { key: 'result', header: 'Resultado' }
    ];
  }

  getColumnValue(item: any, key: string): any {
    if (!key) return null;
    const parts = key.split('.');
    let cur = item;
    for (const p of parts) {
      if (cur == null) return null;
      cur = cur[p];
    }
    return cur;
  }

  formatSummaryValue(v: any): string {
    if (v == null) return '-';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  summaryEntries(): Array<[string, any]> {
    if (!this.run?.summary) return [];
    return Object.entries(this.run.summary);
  }
}
